import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaEnvelope } from 'react-icons/fa';
import { signupRequestsAPI } from '../utils/api';
import './Auth.css';

// Public page: team member requests access.
// After approval, they get an invite link/token to open /signup.
const STORAGE_KEY = 'menubazzar_signup_request_id';

function saveRequestId(id) {
  localStorage.setItem(STORAGE_KEY, String(id));
}

function getRequestId() {
  return localStorage.getItem(STORAGE_KEY);
}

function clearRequestId() {
  localStorage.removeItem(STORAGE_KEY);
}

function SignupAccess() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // request | waiting
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [request, setRequest] = useState(null);

  // If the user already requested earlier, resume the waiting screen.
  useEffect(() => {
    const existingId = getRequestId();
    if (existingId) {
      setStep('waiting');
      setRequest({ id: existingId });
      setMessage('Resuming your request…');
    }
  }, []);

  // Poll for status while waiting.
  useEffect(() => {
    let timer;

    const poll = async () => {
      const id = request?.id || getRequestId();
      if (!id) return;

      try {
        const res = await signupRequestsAPI.get(id);
        const row = res?.data?.request;
        if (row) {
          setRequest(row);

          if (row.status === 'pending') {
            setMessage('Request pending. Please wait for approval.');
          } else if (row.status === 'approved') {
            setMessage('Approved! Please ask the team head for the invite link to complete signup.');
          } else if (row.status === 'denied') {
            setMessage('Request denied. Please contact the team head.');
            clearRequestId();
          } else if (row.status === 'expired') {
            setMessage('Request expired. Please request again.');
            clearRequestId();
          } else if (row.status === 'used') {
            setMessage('Invite already used. If you still need access, request again.');
            clearRequestId();
          }
        }
      } catch (e) {
        // Don’t spam error UI; keep it minimal.
      }
    };

    if (step === 'waiting') {
      poll();
      timer = setInterval(poll, 3000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [step, request?.id]);

  const handleRequest = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    const normalized = String(email || '').trim().toLowerCase();
    if (!normalized) {
      setError('Email is required');
      return;
    }

    setLoading(true);
    try {
      const res = await signupRequestsAPI.request(normalized);
      const reqRow = res?.data?.request;
      if (!reqRow?.id) throw new Error('Failed to create request');

      saveRequestId(reqRow.id);
      setRequest(reqRow);
      setMessage('Request sent. Please wait for approval from the team head.');
      setStep('waiting');
    } catch (err) {
      setError(err.message || 'Failed to send request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Request Access to Signup</h1>
          <p>Signup is invite-only. Request access and wait for approval.</p>
        </div>

        {error && <div className="auth-error">{error}</div>}
        {message && <div className="auth-success">{message}</div>}

        {step === 'request' ? (
          <form className="auth-form" onSubmit={handleRequest}>
            <div className="form-group">
              <label htmlFor="email">
                <FaEnvelope /> Your Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                }}
                placeholder="Enter your email"
                required
                autoComplete="email"
              />
            </div>

            <button type="submit" className="auth-submit-btn" disabled={loading}>
              {loading ? 'Sending...' : 'Request Access'}
            </button>
          </form>
        ) : (
          <div className="auth-form">
            <p style={{ marginBottom: 10 }}>
              Your request is pending approval. Once approved, the team head will share an invite link with you.
            </p>
            <button
              type="button"
              className="auth-submit-btn"
              onClick={() => {
                clearRequestId();
                setRequest(null);
                setStep('request');
                setMessage('');
                setError('');
              }}
            >
              Start over
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default SignupAccess;
